"use client";

import type { SyntheticEvent } from "react";
import Link from "next/link";
import {
  ArrowUpRight,
  CalendarDays,
  Download,
  Globe,
  Lock,
  MapPin,
  Phone,
  Tag,
  Ticket
} from "lucide-react";
import { PinkImageFallback } from "@/components/PinkImageFallback";
import type { BuzzBusiness, BuzzDeal, BuzzEvent, BuzzLockedTeaser } from "@/components/local-buzz/types";
import { cn } from "@/lib/utils";

function revealFallback(event: SyntheticEvent<HTMLImageElement>) {
  const fallback = event.currentTarget.previousElementSibling;
  if (fallback instanceof HTMLElement) {
    fallback.removeAttribute("aria-hidden");
  }
  event.currentTarget.setAttribute("aria-hidden", "true");
  event.currentTarget.style.opacity = "0";
}

function CardMedia({
  src,
  alt,
  headline,
  footer,
  topLabel,
  aspect = "aspect-[4/3]"
}: {
  src?: string | null;
  alt: string;
  headline: string;
  footer?: string;
  topLabel?: string;
  aspect?: string;
}) {
  return (
    <div className={cn("relative overflow-hidden", aspect)}>
      <PinkImageFallback
        ariaHidden={Boolean(src)}
        ariaLabel={`${alt || headline} image unavailable`}
        headline={headline}
        footer={footer}
        topLabel={topLabel}
        className="absolute inset-0 h-full w-full"
      />
      {src ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="relative z-10 h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
          onError={revealFallback}
        />
      ) : null}
    </div>
  );
}

function CornerTag({ label, category }: { label: string; category: string }) {
  return (
    <div className="absolute left-3 top-3 z-10 flex flex-col items-start gap-1.5">
      <span className="rounded-full bg-brand px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.2em] text-white shadow-sm">
        {label}
      </span>
      <span className="rounded-full bg-ink/75 px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-white/95 backdrop-blur-sm">
        {category}
      </span>
    </div>
  );
}

function priceLead(value: string) {
  const middleDot = String.fromCharCode(183);
  const mojibakeDot = `${String.fromCharCode(194)}${middleDot}`;
  return value.split(middleDot)[0]?.split(mojibakeDot)[0]?.trim() || value;
}

export function LocalBuzzLockedCard({ teaser, redirectTo }: { teaser: BuzzLockedTeaser; redirectTo: string }) {
  return (
    <article className="group flex min-h-full flex-col overflow-hidden rounded-xl border border-border bg-card transition hover:-translate-y-0.5 hover:border-brand">
      <div className="relative overflow-hidden">
        <CardMedia src={teaser.imageUrl} alt="" headline={teaser.title} footer={teaser.entityType} />
        <CornerTag label="Members" category={teaser.entityType} />
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-display text-lg font-black leading-tight">{teaser.title}</h3>
        <div className="mt-3 rounded-xl border border-dashed border-brand/40 bg-brand/5 p-4 text-center text-xs text-muted-foreground">
          <Lock className="mx-auto mb-2 h-4 w-4 text-brand" />
          Sign in to see full details.
        </div>
        <div className="mt-auto grid grid-cols-2 gap-2 pt-4">
          <Link href={`/auth/login?redirectTo=${encodeURIComponent(redirectTo)}`} className="rounded-md border border-border px-3 py-2 text-center text-[11px] font-black uppercase tracking-[0.14em] hover:border-brand hover:text-brand">
            Sign in
          </Link>
          <Link href={`/auth/signup?redirectTo=${encodeURIComponent(redirectTo)}`} className="rounded-md bg-brand px-3 py-2 text-center text-[11px] font-black uppercase tracking-[0.14em] text-white">
            Sign up
          </Link>
        </div>
      </div>
    </article>
  );
}

export function LocalBuzzEventCard({
  event,
  basePath = "/events",
  label = "Event"
}: {
  event: BuzzEvent;
  basePath?: string;
  label?: string;
}) {
  const href = `${basePath}/${event.slug}`;
  if (event.lockedTeaser) return <LocalBuzzLockedCard teaser={event.lockedTeaser} redirectTo={href} />;

  return (
    <Link
      href={href}
      className="group flex min-h-full flex-col overflow-hidden rounded-xl border border-border bg-card transition hover:-translate-y-0.5 hover:border-brand"
    >
      <div className="relative overflow-hidden">
        <CardMedia
          src={event.imageUrl}
          alt={event.title}
          headline={event.title}
          footer={`${event.dateLabel} \u00b7 ${event.suburb}`}
        />
        <CornerTag label={label} category={event.category} />
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-display text-lg font-black leading-tight">{event.title}</h3>
        <p className="mt-1 line-clamp-2 text-[13px] text-muted-foreground">{event.tagline}</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5 text-brand" />
            {event.dateLabel}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-brand" />
            {event.suburb}
          </span>
        </div>
        <div className="mt-auto flex items-center justify-between border-t border-border pt-3">
          <span className="inline-flex items-center gap-1 text-[12px] font-bold">
            <Ticket className="h-3.5 w-3.5 text-brand" />
            {priceLead(event.priceLabel)}
          </span>
          <ArrowUpRight className="h-4 w-4 text-brand transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </div>
    </Link>
  );
}

export function LocalBuzzDealCard({ deal }: { deal: BuzzDeal }) {
  if (deal.lockedTeaser) return <LocalBuzzLockedCard teaser={deal.lockedTeaser} redirectTo={`/deals/${deal.slug}`} />;

  return (
    <Link href={`/deals/${deal.slug}`} className="group flex min-h-full flex-col overflow-hidden rounded-xl border border-border bg-card transition hover:-translate-y-0.5 hover:border-brand">
      <div className="relative overflow-hidden">
        <CardMedia
          src={deal.imageUrl}
          alt={deal.merchant}
          headline={deal.offer}
          footer={`${deal.merchant} \u00b7 ${deal.suburb}`}
        />
        <CornerTag label={deal.type === "intro-offer" ? "Intro Offer" : "Deal"} category={deal.category} />
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-display text-lg font-black leading-tight">{deal.title}</h3>
        <p className="mt-1 font-script text-base leading-tight text-brand">{deal.merchant}</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-brand" />{deal.suburb}</span>
          <span className="inline-flex items-center gap-1.5"><Tag className="h-3.5 w-3.5 text-brand" />{deal.priceFrom ?? deal.offer}</span>
        </div>
        <div className="mt-auto flex items-center justify-between border-t border-border pt-3">
          <span className="inline-flex items-center gap-1 text-[12px] font-bold">{deal.offer}</span>
          <ArrowUpRight className="h-4 w-4 text-brand transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </div>
    </Link>
  );
}

export function LocalBuzzBusinessCard({ business }: { business: BuzzBusiness }) {
  if (business.lockedTeaser) return <LocalBuzzLockedCard teaser={business.lockedTeaser} redirectTo={`/services/directory/${business.slug}`} />;

  return (
    <article className="group flex min-h-full flex-col overflow-hidden rounded-xl border border-border bg-card transition hover:-translate-y-0.5 hover:border-brand">
      <Link href={`/services/directory/${business.slug}`} className="relative block overflow-hidden">
        <CardMedia
          src={business.imageUrl}
          alt={business.name}
          headline={business.name}
          aspect="aspect-[16/10]"
          topLabel={business.category}
        />
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
          {business.subcategory ?? business.category}
        </div>
        <h3 className="mt-1.5 font-display text-lg font-black leading-tight">{business.name}</h3>
        <p className="mt-1 line-clamp-2 text-[13px] text-muted-foreground">{business.description}</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-brand" />{business.suburb}</span>
          {business.phone ? <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-brand" />{business.phone}</span> : null}
        </div>
        <div className="mt-auto flex items-center justify-between border-t border-border pt-3">
          {business.websiteUrl ? (
            <a href={business.websiteUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[12px] font-bold text-brand hover:underline">
              <Globe className="h-3.5 w-3.5" /> Visit website
            </a>
          ) : (
            <Link href={`/services/directory/${business.slug}`} className="inline-flex items-center gap-1.5 text-[12px] font-bold text-brand hover:underline">
              View details
            </Link>
          )}
          <ArrowUpRight className="h-4 w-4 text-brand" />
        </div>
      </div>
    </article>
  );
}

export function VoucherLink({ href }: { href: string | null }) {
  if (!href) return null;
  return (
    <a href={href} download className="inline-flex items-center gap-1.5 rounded-md border border-brand/40 px-3 py-2 text-[12px] font-bold text-brand hover:bg-brand/10">
      <Download className="h-3.5 w-3.5" /> Voucher
    </a>
  );
}
